import { PropsWithChildren, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { twJoin } from 'tailwind-merge';

import EmptyMessage from '@/components/shared/EmptyMessage';
import ErrorMessage from '@/components/shared/ErrorMessage';
import CourseTrackList from '@/components/student-dashboard/CourseTrackList';
import CourseTrackSkeleton, {
  COURSE_TRACK_CARD_WIDTH,
  COURSE_TRACK_ITEM_MIN_HEIGHT,
} from '@/components/student-dashboard/CourseTrackSkeleton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useStudentDashboard } from '@/data/context/StudentDashboardContext';
import useStudentCourseTrack from '@/data/hook/student-dashboard/useStudentCourseTrack';
import { getBannerImg } from '@/utils/coursePath';
import { COURSE_TRACK_PAGE_SIZE } from '@/utils/student-dashboard/courseTrackLesson';

export default function StudentCourseTrackSection() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.courseTrack',
  });
  const { activeCourseProgress, isLoading: isLoadingContext } =
    useStudentDashboard();

  const { lessons, unitsHref, hasNextPage, fetchNextPage, isLoading, error } =
    useStudentCourseTrack({
      courseProgressId: activeCourseProgress?.id,
      klassId: activeCourseProgress?.klassId,
      slug: activeCourseProgress?.coursePath.slug,
    });

  const lessonsWithBanner = useMemo(() => {
    if (!activeCourseProgress) return lessons;

    const courseBanner = getBannerImg(activeCourseProgress.coursePath);

    return lessons.map(lesson => ({
      ...lesson,
      bannerImg: lesson.bannerImg || courseBanner,
    }));
  }, [lessons, activeCourseProgress]);

  if (isLoadingContext || isLoading)
    return (
      <SectionShell title={t('title')}>
        <LoadingView />
      </SectionShell>
    );

  if (error)
    return (
      <SectionShell title={t('title')}>
        <ErrorMessage text={t('loadError')} className="h-24" />
      </SectionShell>
    );

  if (!activeCourseProgress) return null;

  if (lessons.length === 0)
    return (
      <SectionShell title={t('title')}>
        <EmptyMessage
          text={t('empty')}
          className={twJoin(
            'rounded-3xl bg-base-200 p-6',
            COURSE_TRACK_ITEM_MIN_HEIGHT,
          )}
        />
        <ConditionalRenderer condition={!!unitsHref}>
          <UnitsLink href={unitsHref ?? ''} className="self-end">
            {t('seeAllUnits')}
          </UnitsLink>
        </ConditionalRenderer>
      </SectionShell>
    );

  return (
    <SectionShell title={t('title')}>
      <CourseTrackList
        lessons={lessonsWithBanner}
        unitsHref={unitsHref}
        hasNextPage={hasNextPage}
        onReachEnd={fetchNextPage}
      />
      <ConditionalRenderer condition={!!unitsHref}>
        <UnitsLink href={unitsHref ?? ''} className="hidden self-end md:flex">
          {t('seeAll')}
        </UnitsLink>
      </ConditionalRenderer>
    </SectionShell>
  );
}

type SectionShellProps = PropsWithChildren<{
  title: string;
}>;

function SectionShell({ title, children }: SectionShellProps) {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="flex min-w-0 flex-col gap-4">
      <h2 id={titleId} className="text-base font-bold text-base-content">
        {title}
      </h2>
      {children}
    </section>
  );
}

type UnitsLinkProps = PropsWithChildren<{
  href: string;
  className?: string;
}>;

function UnitsLink({ href, className, children }: UnitsLinkProps) {
  return (
    <Link
      to={href}
      className={twJoin(
        'flex items-center gap-1.5 text-xs font-semibold text-base-content hover:underline',
        className,
      )}
    >
      {children}
      <ArrowRightIcon className="size-3" />
    </Link>
  );
}

function LoadingView() {
  return (
    <div className="flex w-full gap-6 overflow-hidden">
      {Array.from({ length: COURSE_TRACK_PAGE_SIZE }, (_, index) => (
        <CourseTrackSkeleton key={index} className={COURSE_TRACK_CARD_WIDTH} />
      ))}
    </div>
  );
}
